import React, { useEffect, useState, useContext } from 'react';
import { Box, CloseButton, Flex, IconButton } from 'pcln-design-system';
import styled from 'styled-components';
import Popover from 'pcln-popover';
import { ChatbotDataContext } from '../../../context/ChatbotDataContext';
import RealtimeIcon from '../../../svgs/RealtimeIcon';
import RealtimeToolTipInfo from '../../../Voice/RealtimeToolTipInfo';
import MicIcon from '../../../svgs/MicIcon';
import { useRealTimeContext } from '../../../hooks/useRealTimeContext';
import RealtimeInfoModal from './RealtimeInfoModal';
const MicContainer = /*#__PURE__*/styled(Box).withConfig({
  displayName: "VoiceControlButton__MicContainer",
  componentId: "sc-xd3wlc-0"
})(["height:49px;width:49px;z-index:1000;"]);
const PopoverContent = /*#__PURE__*/styled(Flex).withConfig({
  displayName: "VoiceControlButton__PopoverContent",
  componentId: "sc-xd3wlc-1"
})(["z-index:1000;"]);
/**
 * A button component that controls voice/realtime functionality in the chatbot.
 * Displays a tooltip with information about voice mode and realtime features.
 * Manages tooltip visibility state and persistence through localStorage.
 * Renders a mic icon button that toggles realtime mode when clicked.
 * @public
 */
const VoiceControlButton = ({
  isExpandableTextArea
}) => {
  const {
    enableVoiceTooltip
  } = useContext(ChatbotDataContext);
  const {
    setStartRealTime,
    isRealtime
  } = useRealTimeContext();
  const [isMounted, setIsMounted] = useState(false);
  const [showRealTimeInfoModal, setShowRealTimeInfoModal] = useState(false);
  const [showVoiceTooltip, setShowVoiceTooltip] = useState(true);
  useEffect(() => {
    if (typeof localStorage !== 'undefined' && localStorage.getItem) {
      const userClosedTooltip = localStorage.getItem('userClosedVoiceModeTooltip');
      if (userClosedTooltip !== 'true') {
        setShowVoiceTooltip(true);
      } else {
        setShowVoiceTooltip(false);
      }
    }
  }, []);
  useEffect(() => {
    if (showRealTimeInfoModal) {
      const body = document.querySelector(`body`);
      body.style.setProperty('position', 'static', 'important');
    }
  }, [showRealTimeInfoModal]);
  // Prevent rendering on SSR
  useEffect(() => {
    setIsMounted(true);
  }, []);
  function handleStartRealTime() {
    setStartRealTime(true);
  }
  function handleShowRealTimeInfoModal(value) {
    setShowRealTimeInfoModal(value);
  }
  function closeTooltip() {
    setShowVoiceTooltip(false);
    if (typeof localStorage !== 'undefined' && localStorage.setItem) {
      localStorage.setItem('userClosedVoiceModeTooltip', 'true');
    }
  }
  if (!isMounted) return null;
  if (isExpandableTextArea) {
    return React.createElement(React.Fragment, null, React.createElement(Popover
    // eslint-disable-next-line react/jsx-no-bind
    , {
      // eslint-disable-next-line react/jsx-no-bind
      renderContent: ({
        handleClose
      }) => React.createElement(PopoverContent, {
        id: 'voice-mode-tooltip',
        p: 2,
        style: {
          pointerEvents: 'auto'
        },
        "data-testid": 'voice-tooltip'
      }, React.createElement(Box, {
        height: '40px',
        width: '40px',
        m: 2
      }, React.createElement(RealtimeIcon, null)), React.createElement(RealtimeToolTipInfo, {
        isRealtime: isRealtime,
        handleShowRealTimeInfoModal: handleShowRealTimeInfoModal
      }), React.createElement(Box, null, React.createElement(CloseButton, {
        "data-testid": 'tooltip-close-button',
        onClick: handleClose
      }))),
      idx: 'voiceModeTooltip',
      zIndex: 1000,
      placement: 'top',
      ariaLabel: 'Voice Mode Information',
      width: 310,
      stopPropagation: false,
      toggleIsOpenOnClick: false,
      openOnHover: false,
      openOnFocus: false,
      openOnMount: false,
      onClose: closeTooltip,
      isOpen: showVoiceTooltip && enableVoiceTooltip && !showRealTimeInfoModal
    }, React.createElement(IconButton, {
      onClick: handleStartRealTime,
      "data-testid": 'real-time-record-button',
      icon: React.createElement(RealtimeIcon, {
        size: 'sm'
      })
    })), React.createElement(RealtimeInfoModal, {
      setShowRealTimeInfoModal: setShowRealTimeInfoModal,
      setShowVoiceTooltip: setShowVoiceTooltip,
      showRealTimeInfoModal: showRealTimeInfoModal
    }));
  }
  return React.createElement(React.Fragment, null, React.createElement(Popover
  // eslint-disable-next-line react/jsx-no-bind
  , {
    // eslint-disable-next-line react/jsx-no-bind
    renderContent: ({
      handleClose
    }) => React.createElement(PopoverContent, {
      id: 'voice-mode-tooltip',
      p: 2,
      style: {
        pointerEvents: 'auto'
      },
      "data-testid": 'voice-tooltip'
    }, React.createElement(Box, {
      height: '40px',
      width: '40px',
      m: 2
    }, isRealtime ? React.createElement(RealtimeIcon, null) : React.createElement(MicIcon, {
      width: '30'
    })), React.createElement(RealtimeToolTipInfo, {
      isRealtime: isRealtime,
      handleShowRealTimeInfoModal: handleShowRealTimeInfoModal
    }), React.createElement(Box, null, React.createElement(CloseButton, {
      "data-testid": 'tooltip-close-button',
      onClick: handleClose
    }))),
    idx: 'voiceModeTooltip',
    zIndex: 1000,
    placement: 'top',
    ariaLabel: 'Voice Mode Information',
    width: isRealtime ? 310 : 290,
    stopPropagation: false,
    toggleIsOpenOnClick: false,
    openOnHover: false,
    openOnFocus: false,
    openOnMount: false,
    onClose: closeTooltip,
    isOpen: showVoiceTooltip && enableVoiceTooltip && !showRealTimeInfoModal
  }, React.createElement(MicContainer, null, React.createElement(IconButton, {
    onClick: handleStartRealTime,
    "data-testid": 'real-time-record-button',
    icon: React.createElement(RealtimeIcon, {
      size: 'md'
    })
  }))), React.createElement(RealtimeInfoModal, {
    setShowRealTimeInfoModal: setShowRealTimeInfoModal,
    setShowVoiceTooltip: setShowVoiceTooltip,
    showRealTimeInfoModal: showRealTimeInfoModal
  }));
};
export default VoiceControlButton;